import { useIntl } from 'react-intl'
import { isRewardFareClass, type FareClassWithReward } from 'constants/flight'
import { getCharterFareCabinLabel } from 'hooks/Flights/useCharterFlightLabels'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOpenCharterFareClassModal from 'hooks/OfferPage/useOpenCharterFareClassModal'

interface Props {
  offer?: App.Offer
  fareClass: FareClassWithReward
  className?: string
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
}

function CharterFlightFareClassSelect({ offer, fareClass, className, checkInDate, checkOutDate, occupancies }: Props) {
  const intl = useIntl()
  const { canOpen, open } = useOpenCharterFareClassModal({ offer, fareClass, checkInDate, checkOutDate, occupancies })

  if (!canOpen) {
    return null
  }

  const cabinLabel = getCharterFareCabinLabel(intl, fareClass)
  const value = isRewardFareClass(fareClass)
    ? <b>{intl.formatMessage(
        {
          id: 'jnxEIPY1CCyg-STqwYK9',
          defaultMessage: '{cabin} {loyaltyProgramName} Rewards',
          description: 'Charter flight fare class selector value when a reward variant is selected; cabin is the base cabin name (Economy, Economy Plus, Business), loyaltyProgramName is the configured loyalty program (e.g. Société).',
        },
        { cabin: cabinLabel, loyaltyProgramName: getLoyaltyProgramDisplayName() },
      )}
    </b>
    : <b>{cabinLabel}</b>

  return (
    <OfferFilterDropdownBox
      className={className}
      data-testid="fare-class-select"
      onClick={open}
      icon={<LineSeatIcon />}
      label={intl.formatMessage({
        id: 'qziWiR2Ojnm1pUMoGMst',
        defaultMessage: 'Class',
        description: 'Label for charter flight fare class selector in the flights flow',
      })}
      value={value}
      placeholder={intl.formatMessage({
        id: 's-t6Cr_PH8dkvIHHzzGD',
        defaultMessage: 'Select fare class',
        description: 'Placeholder text for charter flight fare class selector',
      })}
    />
  )
}

type MappedProps = Pick<Props, 'offer' | 'fareClass' | 'checkInDate' | 'checkOutDate' | 'occupancies'>

export default withOfferPageState((state): MappedProps => ({
  offer: state.offer,
  fareClass: state.fareClass,
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  occupancies: state.rooms,
}), CharterFlightFareClassSelect)
